import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  MapPin,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const COLOMBES_BESTSELLER_SLUGS = [
  "carton-standard-renforce",
  "carton-24-assiettes",
  "pack-cartons-demenagement-t3",
  "devidoir-pistolet-adhesif",
  "film-etirable",
  "couverture-demenagement",
];

const Sections = ({ city }) => {
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    fetchProducts()
      .then((res) => {
        const all = res || [];
        const picked = COLOMBES_BESTSELLER_SLUGS
          .map((s) => all.find((p) => p.slug === s))
          .filter(Boolean);
        setBestsellers(picked);
      })
      .catch(() => setBestsellers([]));
  }, []);

  return (
    <>
      {/* ======= SECTION 2 — Livraison à Colombes + 3 engagements ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à votre porte
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Vos fournitures à votre porte : livraison de cartons à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Évitez la corvée de charger des paquets d&apos;emballages
              volumineux dans votre véhicule ou de courir les magasins de
              bricolage. Nous acheminons l&apos;intégralité de votre commande
              directement à votre adresse à {city.name}.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Zéro intermédiaire",
                body: "Notre propre société gère le transport avec ses propres chauffeurs en Île-de-France. Vos produits ne sont jamais externalisés ni malmenés.",
                Icon: Truck,
              },
              {
                title: "Intégrité garantie",
                body: "Vos fournitures voyagent à l'abri des intempéries dans nos véhicules fermés, vous assurant un matériel propre, sec et prêt à être assemblé.",
                Icon: ShieldCheck,
              },
              {
                title: "Gestion locale",
                body: "Un ancrage régional unique qui nous permet de respecter nos créneaux horaires pour ne jamais bloquer votre planning de préparation.",
                Icon: MapPin,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Verrouillage : Accessoires de fermeture ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Accessoires de fermeture
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Verrouillage haute performance : nos accessoires de fermeture
              et leur utilité
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Un carton ultra-résistant perd toute son efficacité s&apos;il
              n&apos;est pas correctement scellé. Pour sécuriser
              l&apos;assemblage de vos boîtes, nous mettons à votre
              disposition une gamme d&apos;adhésifs et de dévidoirs
              professionnels indispensables.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Les rouleaux d'adhésif technique",
                body: "Dotés d'une masse adhésive à forte adhérence immédiate, ils fusionnent avec la fibre du carton. Ils maintiennent les rabats parfaitement clos, même sous la pression d'objets lourds ou d'un stockage prolongé.",
                Icon: Tag,
              },
              {
                title: "Les dévidoirs ergonomiques (pistolets à scotch)",
                body: "Cet outil de pro change radicalement la donne. Il permet d'appliquer, de tendre et de couper le ruban d'une seule main en une seconde, vous évitant de perdre le fil du rouleau ou de vous couper les doigts.",
                Icon: Scissors,
              },
              {
                title: "Utilité technique",
                body: "Au-delà de la solidité mécanique, un scellage hermétique de vos cartons crée une barrière étanche contre la poussière, l'humidité des boxes et les frottements durant le transport.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/fermeture-accessoires"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-colombes-cta-fermeture"
            >
              <Scissors className="h-4 w-4" strokeWidth={2.6} />
              Voir les adhésifs et pistolets
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Plébiscités par nos clients (best-sellers) ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#FF7A00" }}>
              Top ventes locales
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Plébiscités par nos clients : les indispensables du déménagement
              à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Quels sont les équipements les plus choisis par les habitants
              de {city.name} pour sécuriser leurs transferts&nbsp;? Nous avons
              regroupé ici les produits phares de notre catalogue, plébiscités
              pour leur robustesse sur le terrain et leur excellent rapport
              qualité-prix. Découvrez les outils incontournables adoptés par
              les particuliers et les entreprises de la région pour emballer
              sans stress.
            </p>
          </div>

          {bestsellers.length === 0 ? (
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="aspect-square bg-white animate-pulse rounded-2xl border border-[#cfdbe6]"
                />
              ))}
            </div>
          ) : (
            <div
              className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
              data-testid="city-colombes-bestsellers"
            >
              {bestsellers.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-colombes-cta-shop"
            >
              Voir tout le catalogue
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-colombes-cta-kits"
            >
              <Layers className="h-4 w-4" strokeWidth={2.6} />
              Choisir un kit
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-colombes-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
